<template>
  <div class="page">
    <!-- 헤더 -->
    <header class="top-header">
      <div class="header-inner">
        <div class="header-left">
          <div class="brand-icon">
            <img
              src="@/assets/icon/tech-center-icon.jpg"
              alt="테크센터 아이콘"
            />
          </div>
          <div class="brand-text">
            <div class="brand-title">부산광역시교육청 테크센터</div>
            <div class="brand-sub">(주) 디아이앤씨</div>
          </div>
        </div>

        <nav class="header-center">
          <button
            type="button"
            class="menu-btn"
            :class="{ active: activeMenu === 'lookup' }"
            @click="activeMenu = 'lookup'"
          >
            A/S조회
          </button>
          <button
            type="button"
            class="menu-btn"
            :class="{ active: activeMenu === 'faq' }"
            @click="goFaq"
          >
            FAQ
          </button>
        </nav>

        <div class="header-right"></div>
      </div>
    </header>

    <!-- 컨텐츠 -->
    <main class="content-wrap">
      <section class="content-card">
        <div class="content-head">
          <h1 class="page-title">A/S조회</h1>
          <p v-if="hasSearched" class="result-desc">
            클릭 시, 상세내용을 확인할 수 있습니다.
          </p>
        </div>

        <!-- 검색 화면 -->
        <section v-if="!hasSearched" class="search-panel">
          <div class="search-icon-wrap">
            <div class="search-icon-circle">
              <img
                src="@/assets/icon/web-search-engine.png"
                alt="검색"
                class="search-icon-img"
              />
            </div>
          </div>

          <p class="search-desc">
            ARS, 카카오채널로 접수한 '학교 / 기관명'과 '접수자 이름'으로
            진행상황을 조회할 수 있습니다.
          </p>

          <form class="search-form" @submit.prevent="handleSearch">
            <div class="search-field">
              <input
                ref="orgNameInputRef"
                v-model.trim="searchForm.orgName"
                type="text"
                class="search-input"
                :class="{ 'is-error': fieldErrors.orgName }"
                placeholder="학교 / 기관명"
                @input="clearFieldError('orgName')"
                @keyup.enter="handleSearch"
              />
              <p v-if="fieldErrors.orgName" class="field-error-text">
                {{ fieldErrors.orgName }}
              </p>
            </div>

            <div class="search-field">
              <input
                ref="requesterNameInputRef"
                v-model.trim="searchForm.requesterName"
                type="text"
                class="search-input"
                :class="{ 'is-error': fieldErrors.requesterName }"
                placeholder="접수자 이름"
                @input="clearFieldError('requesterName')"
                @keyup.enter="handleSearch"
              />
              <p v-if="fieldErrors.requesterName" class="field-error-text">
                {{ fieldErrors.requesterName }}
              </p>
            </div>

            <button type="submit" class="search-submit" :disabled="isLoading">
              {{ isLoading ? "조회중..." : "조회" }}
            </button>
          </form>

          <div
            class="search-guide-box"
            :class="{ 'is-error': guideState === 'error' }"
          >
            <div class="search-guide-title">
              {{
                guideState === "error"
                  ? "검색어를 다시 확인해 주세요"
                  : "검색 안내"
              }}
            </div>

            <template v-if="guideState === 'error'">
              <p class="search-guide-alert">
                {{ guideMessage }}
              </p>
            </template>

            <template v-else>
              <ul class="search-guide-list">
                <li>학교 / 기관명과 접수자 이름은 구체적으로 입력해 주세요.</li>
                <li>
                  포괄적인 단어만 입력한 검색은 제한됩니다.
                  <span class="bad-example">
                    (예: 학교, 고등학교, 중학교, 초등학교, 선생님)
                  </span>
                </li>
              </ul>
            </template>

            <div v-if="!ENABLE_SEARCH_GUARD" class="search-guard-off-badge">
              테스트 모드: 검색 제한이 비활성화되어 있습니다.
            </div>
          </div>

          <div class="notice-list">
            <div class="notice-item notice-item--accent">
              <span class="notice-icon">i</span>
              <div class="notice-text">
                상담 운영시간 : 평일 오전 8시 ~ 오후 5시
                <span class="notice-sub">
                  (점심시간 11시 30분 ~ 13시 / 토요일·일요일·공휴일 휴무)
                </span>
              </div>
            </div>

            <div class="notice-item">
              <span class="notice-icon">i</span>
              <div class="notice-text">
                자세한 문의는 고객지원 번호 <b>1661-1884</b> 로 연락해 주세요.
              </div>
            </div>
          </div>
        </section>

        <!-- 조회 결과 화면 -->
        <section v-else class="result-panel">
          <div class="result-top">
            <div class="result-head-left">
              <h2 class="result-title">조회 결과</h2>
              <p class="result-desc">
                학교 / 기관명: <b>{{ searchedInfo.orgName || "-" }}</b>
                <span class="divider-dot">·</span>
                접수자 이름: <b>{{ searchedInfo.requesterName || "-" }}</b>
              </p>
            </div>

            <div class="result-tools">
              <!-- todo -->
              <div class="month-range-wrap desktop-only">
                <input
                  v-model="dateFilter.startDate"
                  type="month"
                  class="tool-input month-input"
                  @change="handleDateFilterChange"
                />
                <span class="range-tilde">~</span>
                <input
                  v-model="dateFilter.endDate"
                  type="month"
                  class="tool-input month-input"
                  @change="handleDateFilterChange"
                />
              </div>
              <div class="month-range-wrap mobile-only">
                <div class="month-range-wrap">
                  <input
                    v-model="dateFilter.startDate"
                    type="month"
                    class="tool-input month-input"
                    @change="handleDateFilterChange"
                  />
                  <label style="word-break: keep-all">부터</label>
                </div>
              </div>
              <div class="month-range-wrap mobile-only">
                <div class="month-range-wrap">
                  <input
                    v-model="dateFilter.endDate"
                    type="month"
                    class="tool-input month-input"
                    @change="handleDateFilterChange"
                  />
                  <label style="word-break: keep-all">까지</label>
                </div>
              </div>

              <button
                type="button"
                class="tool-btn"
                :disabled="isCopyingUrl"
                @click="copySearchUrl"
              >
                {{ isCopyingUrl ? "복사중..." : "URL복사" }}
              </button>

              <button type="button" class="reset-btn" @click="resetSearch">
                초기화
              </button>
            </div>
          </div>

          <div
            v-if="showSchoolSelector && schoolOptions.length"
            class="school-selector-card"
          >
            <div class="school-selector-title">
              입력하신 검색어와 일치하는 기관이 여러 개 있습니다.
            </div>
            <div class="school-selector-desc">
              정확한 조회를 위해 기관명을 선택해 주세요.
            </div>

            <div class="school-selector-list">
              <button
                type="button"
                class="school-selector-btn"
                :class="{ active: !selectedSchoolFilter }"
                @click="applySchoolFilter('')"
              >
                전체
              </button>

              <button
                v-for="school in schoolOptions"
                :key="school"
                type="button"
                class="school-selector-btn"
                :class="{ active: selectedSchoolFilter === school }"
                @click="applySchoolFilter(school)"
              >
                {{ school }}
              </button>
            </div>
          </div>

          <div v-if="isLoading" class="result-loading-wrap">
            <div class="loading-box">
              <span class="spinner"></span>
              <span>데이터를 불러오는 중입니다.</span>
            </div>
          </div>

          <div v-else-if="!hasAnyResult" class="empty-result-card">
            검색 결과가 없습니다.
          </div>

          <template v-else>
            <!-- 블랜디드 -->
            <section v-if="filteredBlendRows.length" class="result-section">
              <div class="section-head">
                <h3 class="section-title-lg">블랜디드 조회결과</h3>
                <div class="section-count">
                  총 {{ filteredBlendRows.length }}건
                </div>
              </div>

              <div class="desktop-only">
                <div class="table-wrap">
                  <table class="intake-table">
                    <thead>
                      <tr>
                        <th
                          v-for="col in visibleListColumns"
                          :key="`blend-head-${col.key}`"
                          :class="col.className"
                        >
                          {{ col.label }}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="row in displayBlendRows"
                        :key="row.id"
                        class="data-row"
                        :class="{
                          active: selectedRow?.id === row.id && detailOpen
                        }"
                        @click="openDetail(row)"
                      >
                        <td
                          v-for="col in visibleListColumns"
                          :key="`blend-row-${row.id}-${col.key}`"
                        >
                          <div
                            class="ellipsis"
                            :class="{ 'wide-text': col.multiline }"
                            :title="col.value(row)"
                          >
                            <template v-if="col.key === 'progressStatus'">
                              <span
                                class="status-chip"
                                :class="statusClass(row.progressStatus)"
                              >
                                {{ col.value(row) }}
                              </span>
                            </template>
                            <template v-else>
                              {{ col.value(row) }}
                            </template>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="blendTotalPages > 1" class="pagination">
                  <button
                    type="button"
                    class="page-btn"
                    :disabled="blendPage === 1"
                    @click="blendPage--"
                  >
                    이전
                  </button>

                  <button
                    v-for="page in blendTotalPages"
                    :key="`blend-page-${page}`"
                    type="button"
                    class="page-number"
                    :class="{ active: blendPage === page }"
                    @click="blendPage = page"
                  >
                    {{ page }}
                  </button>

                  <button
                    type="button"
                    class="page-btn"
                    :disabled="blendPage === blendTotalPages"
                    @click="blendPage++"
                  >
                    다음
                  </button>
                </div>
              </div>

              <div class="mobile-only">
                <div class="mobile-list">
                  <article
                    v-for="row in displayBlendRows"
                    :key="row.id"
                    class="mobile-item"
                    @click="openDetail(row)"
                  >
                    <div class="mobile-top">
                      <div class="mobile-school">
                        {{ row.schoolName || "-" }}
                      </div>
                      <span
                        class="status-chip"
                        :class="statusClass(row.progressStatus)"
                      >
                        {{ displayProgressStatus(row.progressStatus) }}
                      </span>
                    </div>

                    <div class="mobile-meta">
                      <div
                        v-for="col in mobileMetaColumns"
                        :key="`blend-mobile-meta-${row.id}-${col.key}`"
                      >
                        <b>{{ col.label }}</b> {{ col.value(row) }}
                      </div>
                    </div>

                    <div
                      v-for="col in mobileBlockColumns"
                      :key="`blend-mobile-block-${row.id}-${col.key}`"
                      class="mobile-issue"
                    >
                      <b>{{ col.label }}</b
                      ><br />
                      {{ col.value(row) }}
                    </div>
                  </article>
                </div>

                <div
                  v-if="blendTotalPages > 1"
                  class="pagination pagination-mobile"
                >
                  <button
                    type="button"
                    class="page-btn"
                    :disabled="blendPage === 1"
                    @click="blendPage--"
                  >
                    이전
                  </button>
                  <span class="page-summary"
                    >{{ blendPage }} / {{ blendTotalPages }}</span
                  >
                  <button
                    type="button"
                    class="page-btn"
                    :disabled="blendPage === blendTotalPages"
                    @click="blendPage++"
                  >
                    다음
                  </button>
                </div>
              </div>
            </section>

            <!-- 테크센터 -->
            <section v-if="filteredTechRows.length" class="result-section">
              <div class="section-head">
                <h3 class="section-title-lg">테크센터 조회결과</h3>
                <div class="section-count">
                  총 {{ filteredTechRows.length }}건
                </div>
              </div>

              <div class="desktop-only">
                <div class="table-wrap">
                  <table class="intake-table">
                    <thead>
                      <tr>
                        <th
                          v-for="col in visibleListColumns"
                          :key="`tech-head-${col.key}`"
                          :class="col.className"
                        >
                          {{ col.label }}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr
                        v-for="row in displayTechRows"
                        :key="row.id"
                        class="data-row"
                        :class="{
                          active: selectedRow?.id === row.id && detailOpen
                        }"
                        @click="openDetail(row)"
                      >
                        <td
                          v-for="col in visibleListColumns"
                          :key="`tech-row-${row.id}-${col.key}`"
                        >
                          <div
                            class="ellipsis"
                            :class="{ 'wide-text': col.multiline }"
                            :title="col.value(row)"
                          >
                            <template v-if="col.key === 'progressStatus'">
                              <span
                                class="status-chip"
                                :class="statusClass(row.progressStatus)"
                              >
                                {{ col.value(row) }}
                              </span>
                            </template>
                            <template v-else>
                              {{ col.value(row) }}
                            </template>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="techTotalPages > 1" class="pagination">
                  <button
                    type="button"
                    class="page-btn"
                    :disabled="techPage === 1"
                    @click="techPage--"
                  >
                    이전
                  </button>

                  <button
                    v-for="page in techTotalPages"
                    :key="`tech-page-${page}`"
                    type="button"
                    class="page-number"
                    :class="{ active: techPage === page }"
                    @click="techPage = page"
                  >
                    {{ page }}
                  </button>

                  <button
                    type="button"
                    class="page-btn"
                    :disabled="techPage === techTotalPages"
                    @click="techPage++"
                  >
                    다음
                  </button>
                </div>
              </div>

              <div class="mobile-only">
                <div class="mobile-list">
                  <article
                    v-for="row in displayTechRows"
                    :key="row.id"
                    class="mobile-item"
                    @click="openDetail(row)"
                  >
                    <div class="mobile-top">
                      <div class="mobile-school">
                        {{ row.schoolName || "-" }}
                      </div>
                      <span
                        class="status-chip"
                        :class="statusClass(row.progressStatus)"
                      >
                        {{ displayProgressStatus(row.progressStatus) }}
                      </span>
                    </div>

                    <div class="mobile-meta">
                      <div
                        v-for="col in mobileMetaColumns"
                        :key="`tech-mobile-meta-${row.id}-${col.key}`"
                      >
                        <b>{{ col.label }}</b> {{ col.value(row) }}
                      </div>
                    </div>

                    <div
                      v-for="col in mobileBlockColumns"
                      :key="`tech-mobile-block-${row.id}-${col.key}`"
                      class="mobile-issue"
                    >
                      <b>{{ col.label }}</b
                      ><br />
                      {{ col.value(row) }}
                    </div>
                  </article>
                </div>

                <div
                  v-if="techTotalPages > 1"
                  class="pagination pagination-mobile"
                >
                  <button
                    type="button"
                    class="page-btn"
                    :disabled="techPage === 1"
                    @click="techPage--"
                  >
                    이전
                  </button>
                  <span class="page-summary"
                    >{{ techPage }} / {{ techTotalPages }}</span
                  >
                  <button
                    type="button"
                    class="page-btn"
                    :disabled="techPage === techTotalPages"
                    @click="techPage++"
                  >
                    다음
                  </button>
                </div>
              </div>
            </section>
          </template>
        </section>
      </section>
    </main>

    <!-- 우측 fixed 카카오채널 -->
    <aside class="side-floating-menu">
      <a
        href="https://pf.kakao.com/_ttmMn"
        target="_blank"
        rel="noopener noreferrer"
        class="floating-item"
        title="카카오채널 바로가기"
      >
        <div class="floating-icon floating-icon--qr">
          <img src="@/assets/icon/qrcode_120.png" alt="카카오채널 QR코드" />
        </div>
        <div class="floating-label">카카오채널</div>
      </a>
    </aside>

    <!-- 상세보기 -->
    <transition name="drawer-fade">
      <div v-if="detailOpen" class="drawer-backdrop" @click.self="closeDetail">
        <aside class="detail-drawer">
          <div class="drawer-header">
            <div>
              <div class="drawer-kicker">
                {{ selectedRow?.serviceLabel || "A/S" }}
              </div>
              <h2>A/S 상세보기</h2>
              <p class="drawer-sub">{{ selectedRow?.schoolName || "-" }}</p>
            </div>

            <div class="drawer-header-actions">
              <button
                type="button"
                class="tool-btn drawer-tool-btn"
                :disabled="isGeneratingPdf"
                @click="openPdfPreview"
              >
                {{ isGeneratingPdf ? "PDF 생성중..." : "PDF 미리보기" }}
              </button>

              <button type="button" class="icon-btn" @click="closeDetail">
                ✕
              </button>
            </div>
          </div>

          <div class="drawer-body" v-if="selectedRow">
            <!-- 화면 표시용 -->
            <section class="table-section">
              <h3 class="section-title">상세 정보</h3>

              <!-- 데스크톱/태블릿 -->
              <table class="detail-form-table desktop-detail-table">
                <tbody>
                  <tr
                    v-for="(row, index) in detailRows"
                    :key="`detail-${index}`"
                  >
                    <template v-if="row.left">
                      <th>{{ row.left.label }}</th>
                      <td :class="{ 'multiline-cell': row.left.isMultiline }">
                        {{ row.left.value }}
                      </td>
                    </template>

                    <template v-if="row.right">
                      <th>{{ row.right.label }}</th>
                      <td :class="{ 'multiline-cell': row.right.isMultiline }">
                        {{ row.right.value }}
                      </td>
                    </template>

                    <template v-else>
                      <th></th>
                      <td></td>
                    </template>
                  </tr>

                  <tr v-if="!detailRows.length">
                    <td colspan="4" class="empty-row">
                      표시할 상세 정보가 없습니다.
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- 모바일 -->
              <div class="mobile-detail-list">
                <div
                  v-for="item in detailEntries"
                  :key="`mobile-detail-${item.key}`"
                  class="mobile-detail-item"
                >
                  <div class="mobile-detail-label">
                    {{ item.label }}
                  </div>
                  <div
                    class="mobile-detail-value"
                    :class="{ 'multiline-cell': item.isMultiline }"
                  >
                    {{ item.value }}
                  </div>
                </div>

                <div v-if="!detailEntries.length" class="empty-row">
                  표시할 상세 정보가 없습니다.
                </div>
              </div>
            </section>

            <!-- PDF 캡처용 -->
            <div class="pdf-capture-wrap">
              <section ref="pdfCaptureRef" class="pdf-sheet">
                <div class="pdf-sheet-head">
                  <div class="pdf-brand">부산광역시교육청 테크센터</div>
                  <h1 class="pdf-title">A/S 상세 내역</h1>
                  <div class="pdf-sub">
                    {{ selectedRow?.serviceLabel || "-" }} ·
                    {{ selectedRow?.schoolName || "-" }}
                  </div>
                </div>

                <table class="pdf-detail-table">
                  <tbody>
                    <tr
                      v-for="(row, index) in detailRows"
                      :key="`pdf-detail-${index}`"
                    >
                      <template v-if="row.left">
                        <th>{{ row.left.label }}</th>
                        <td :class="{ 'multiline-cell': row.left.isMultiline }">
                          {{ row.left.value }}
                        </td>
                      </template>

                      <template v-if="row.right">
                        <th>{{ row.right.label }}</th>
                        <td
                          :class="{ 'multiline-cell': row.right.isMultiline }"
                        >
                          {{ row.right.value }}
                        </td>
                      </template>

                      <template v-else>
                        <th></th>
                        <td></td>
                      </template>
                    </tr>

                    <tr v-if="!detailRows.length">
                      <td colspan="4" class="empty-row">
                        표시할 상세 정보가 없습니다.
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="pdf-footer">출력일시 : {{ pdfGeneratedAt }}</div>
              </section>
            </div>
          </div>
        </aside>
      </div>
    </transition>

    <!-- PDF 미리보기 모달 -->
    <transition name="drawer-fade">
      <div
        v-if="pdfPreviewOpen"
        class="pdf-preview-backdrop"
        @click.self="closePdfPreview"
      >
        <div class="pdf-preview-modal">
          <div class="pdf-preview-header">
            <div>
              <strong>PDF 미리보기</strong>
            </div>

            <div class="pdf-preview-actions">
              <button
                type="button"
                class="tool-btn"
                :disabled="isGeneratingPdf"
                @click="downloadCurrentPdf"
              >
                PDF 다운로드
              </button>
              <button type="button" class="icon-btn" @click="closePdfPreview">
                ✕
              </button>
            </div>
          </div>

          <div class="pdf-preview-body">
            <!-- 모바일: 이미지 미리보기 -->
            <img
              v-if="pdfPreviewImageUrl"
              :src="pdfPreviewImageUrl"
              class="pdf-preview-image"
              alt="PDF 이미지 미리보기"
            />

            <div v-else class="pdf-preview-empty">
              미리보기를 불러오지 못했습니다.
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { requestApi } from "@/api";

const showToast = inject("showToast");
const router = useRouter();
const route = useRoute();

const activeMenu = ref("lookup");

const techRows = ref([]);
const blendRows = ref([]);

const selectedRow = ref(null);
const detailOpen = ref(false);
const isLoading = ref(false);
const hasSearched = ref(false);

const showSchoolSelector = ref(false);
const selectedSchoolFilter = ref("");

const techPage = ref(1);
const blendPage = ref(1);
const PAGE_SIZE = 10;

const orgNameInputRef = ref(null);
const requesterNameInputRef = ref(null);

const isCopyingUrl = ref(false);

const pdfCaptureRef = ref(null);
const isGeneratingPdf = ref(false);
const pdfPreviewOpen = ref(false);
const currentPdfBlob = ref(null);
const pdfPreviewImageUrl = ref("");

const searchForm = ref({
  orgName: "",
  requesterName: ""
});

const searchedInfo = ref({
  orgName: "",
  requesterName: ""
});

const dateFilter = ref({
  startDate: "",
  endDate: ""
});

const fieldErrors = ref({
  orgName: "",
  requesterName: ""
});

const guideState = ref("default");
const guideMessage = ref("");

const ENABLE_SEARCH_GUARD = true;

const LIST_COLUMN_VISIBLE = {
  seq: false,
  receivedDate: true,
  schoolName: false,
  customerName: false,
  productName: true,
  manufacturerModel: true,
  classroom: false,
  receiptContent: true,
  visitDate: false,
  progressStatus: true,
  processResult: false,
  completedDate: false
};

const DETAIL_FIELD_VISIBLE = {
  seq: false,
  receivedDate: true,
  schoolName: true,
  customerName: true,
  productName: true,
  manufacturerModel: true,
  classroom: true,
  receiptContent: true,
  visitDate: true,
  progressStatus: true,
  processResult: true,
  completedDate: true
};

const BLOCKED_ORG_TERMS = [
  "고등학교",
  "고등학",
  "등학교",
  "고등",
  "등학",
  "학교",
  "중학교",
  "중학",
  "초등학교",
  "초등학",
  "초등"
];

const BLOCKED_NAME_TERMS = ["선생님", "선생", "생님"];

const MIN_ORG_LENGTH = 2;
const MIN_NAME_LENGTH = 2;

const AS_FIELD_DEFINITIONS = [
  {
    key: "seq",
    label: "순번",
    className: "col-seq",
    value: (row) => displayValue(row.seq)
  },
  {
    key: "receivedDate",
    label: "접수일",
    className: "col-date",
    value: (row) => displayValue(row.receivedDate)
  },
  {
    key: "schoolName",
    label: "학교명",
    className: "col-school",
    value: (row) => displayValue(row.schoolName)
  },
  {
    key: "customerName",
    label: "고객이름",
    className: "col-name",
    value: (row) => displayValue(row.customerName)
  },
  {
    key: "productName",
    label: "제품",
    className: "col-product",
    value: (row) => displayValue(row.productName)
  },
  {
    key: "manufacturerModel",
    label: "제조사(제품 모델명)",
    className: "col-model",
    value: (row) => displayValue(row.manufacturerModel)
  },
  {
    key: "classroom",
    label: "교실",
    className: "col-classroom",
    value: (row) => displayValue(row.classroom)
  },
  {
    key: "receiptContent",
    label: "A/S접수내용",
    className: "col-content",
    value: (row) => displayValue(row.receiptContent),
    multiline: true,
    mobileBlock: true
  },
  {
    key: "processResult",
    label: "처리결과",
    className: "col-result",
    value: (row) => displayValue(row.processResult),
    multiline: true,
    mobileBlock: true
  },
  {
    key: "visitDate",
    label: "방문일자",
    className: "col-date",
    value: (row) => displayValue(row.visitDate)
  },
  {
    key: "progressStatus",
    label: "진행여부",
    className: "col-progress",
    value: (row) => displayProgressStatus(row.progressStatus),
    center: true
  },
  {
    key: "completedDate",
    label: "완료일자",
    className: "col-date",
    value: (row) => displayValue(row.completedDate)
  }
];

const pdfGeneratedAt = computed(() => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mi = String(now.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
});

function goFaq() {
  router.push({ name: "FAQ" });
}

function displayValue(value) {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "-";
  }
  return String(value);
}

function pickValue(item, keys = [], defaultValue = "") {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return defaultValue;
}

function buildRowId(item, index = 0, prefix = "as") {
  const sourceTab = pickValue(item, ["출처탭"], "sample");
  const seq = pickValue(item, ["순번", "번호", "id"], index);
  return `${prefix}-${sourceTab}-${seq}-${index}`;
}

function normalizeProgressStatus(value) {
  const text = String(value || "").trim();
  if (text === "이관") return "진행중";
  if (text === "업체완료") return "완료";
  return text || "";
}

function normalizeKeyword(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, "");
}

function hasEnoughLength(value, min = 2) {
  return normalizeKeyword(value).length >= min;
}

function isBlockedOrgKeyword(value) {
  const keyword = normalizeKeyword(value);
  return BLOCKED_ORG_TERMS.includes(keyword);
}

function isBlockedNameKeyword(value) {
  const keyword = normalizeKeyword(value);
  return BLOCKED_NAME_TERMS.includes(keyword);
}

function clearSearchErrors() {
  fieldErrors.value = {
    orgName: "",
    requesterName: ""
  };
  guideState.value = "default";
  guideMessage.value = "";
}

function clearFieldError(fieldName) {
  fieldErrors.value[fieldName] = "";

  if (!fieldErrors.value.orgName && !fieldErrors.value.requesterName) {
    guideState.value = "default";
    guideMessage.value = "";
  }
}

function focusFirstInvalidField() {
  if (fieldErrors.value.orgName) {
    orgNameInputRef.value?.focus();
    return;
  }

  if (fieldErrors.value.requesterName) {
    requesterNameInputRef.value?.focus();
  }
}

function formatDate(value, type = "full") {
  if (!value) return "";

  const str = String(value).trim();

  if (type === "short") {
    const match = str.match(/^(\d{1,2})[-/.](\d{1,2})$/);
    if (match) {
      const mm = match[1].padStart(2, "0");
      const dd = match[2].padStart(2, "0");
      return `${mm}-${dd}`;
    }
  }

  if (/^\d{2}-\d{2}-\d{2}$/.test(str)) {
    const [yy, mm, dd] = str.split("-");
    return `20${yy}-${mm}-${dd}`;
  }

  if (/^\d{4}\.\d{2}\.\d{2}$/.test(str)) {
    return str.replace(/\./g, "-");
  }

  return str;
}

function normalizeASRow(item, index = 0, serviceType = "tech") {
  const progressStatus = normalizeProgressStatus(
    pickValue(item, ["진행여부", "진행상태"])
  );

  return {
    id: buildRowId(item, index, serviceType),
    serviceType,
    serviceLabel: serviceType === "blend" ? "블랜디드" : "테크센터",

    seq: pickValue(item, ["순번"]),
    receivedDate: formatDate(pickValue(item, ["접수일", "접수일자"]), "short"),
    schoolName: pickValue(item, ["학교명"]),
    customerName: pickValue(item, ["고객이름", "고객명"]),
    productName: pickValue(item, ["제품", "품목"]),
    manufacturerModel: pickValue(item, [
      "제조사(제품 모델명)",
      "제조사(모델명)",
      "제조사/모델명"
    ]),
    classroom: pickValue(item, ["교실", "교실/위치"]),
    receiptContent: pickValue(item, ["A/S접수내용", "접수내용"]),
    visitDate: formatDate(pickValue(item, ["방문일자", "방문일"])),
    progressStatus,
    processResult: pickValue(item, ["처리결과", "조치결과", "점검결과"]),
    completedDate: formatDate(
      pickValue(item, ["완료일자", "완료날짜", "완료일"])
    ),

    raw: item
  };
}

const visibleListColumns = computed(() =>
  AS_FIELD_DEFINITIONS.filter(
    (field) => LIST_COLUMN_VISIBLE[field.key] !== false
  )
);

const visibleDetailFields = computed(() =>
  AS_FIELD_DEFINITIONS.filter(
    (field) => DETAIL_FIELD_VISIBLE[field.key] !== false
  )
);

const mobileMetaColumns = computed(() =>
  visibleListColumns.value.filter((field) => !field.mobileBlock)
);

const mobileBlockColumns = computed(() =>
  visibleListColumns.value.filter((field) => field.mobileBlock)
);

const detailEntries = computed(() => {
  if (!selectedRow.value) return [];

  return visibleDetailFields.value.map((field) => ({
    key: field.key,
    label: field.label,
    value: field.value(selectedRow.value),
    isMultiline: !!field.multiline
  }));
});

const detailRows = computed(() => {
  const pairs = [];
  const items = detailEntries.value;

  for (let i = 0; i < items.length; i += 2) {
    pairs.push({
      left: items[i],
      right: items[i + 1] || null
    });
  }

  return pairs;
});

const allRows = computed(() => [...blendRows.value, ...techRows.value]);

const schoolOptions = computed(() => {
  const uniqueSchools = [
    ...new Set(allRows.value.map((row) => row.schoolName).filter(Boolean))
  ];
  return uniqueSchools.sort((a, b) => a.localeCompare(b, "ko"));
});

const filteredBlendRows = computed(() => {
  if (!selectedSchoolFilter.value) return blendRows.value;
  return blendRows.value.filter(
    (row) => row.schoolName === selectedSchoolFilter.value
  );
});

const filteredTechRows = computed(() => {
  if (!selectedSchoolFilter.value) return techRows.value;
  return techRows.value.filter(
    (row) => row.schoolName === selectedSchoolFilter.value
  );
});

const blendTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredBlendRows.value.length / PAGE_SIZE))
);

const techTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredTechRows.value.length / PAGE_SIZE))
);

const displayBlendRows = computed(() => {
  const start = (blendPage.value - 1) * PAGE_SIZE;
  return filteredBlendRows.value.slice(start, start + PAGE_SIZE);
});

const displayTechRows = computed(() => {
  const start = (techPage.value - 1) * PAGE_SIZE;
  return filteredTechRows.value.slice(start, start + PAGE_SIZE);
});

const hasAnyResult = computed(() => {
  return (
    filteredBlendRows.value.length > 0 || filteredTechRows.value.length > 0
  );
});

watch(filteredBlendRows, () => {
  if (blendPage.value > blendTotalPages.value) {
    blendPage.value = 1;
  }
});

watch(filteredTechRows, () => {
  if (techPage.value > techTotalPages.value) {
    techPage.value = 1;
  }
});

function formatMonthValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
}

function getDefaultDateRange() {
  const now = new Date();
  const currentMonth = formatMonthValue(now);

  return {
    startDate: currentMonth,
    endDate: currentMonth
  };
}

function convertMonthToApiFormat(value) {
  if (!value) return "";
  return value.replace("-", ".");
}

function applySchoolFilter(schoolName) {
  selectedSchoolFilter.value = schoolName;
  techPage.value = 1;
  blendPage.value = 1;
}

async function getASRequest(orgName, requesterName, startDate, endDate) {
  isLoading.value = true;
  techRows.value = [];
  blendRows.value = [];
  showSchoolSelector.value = false;
  selectedSchoolFilter.value = "";
  techPage.value = 1;
  blendPage.value = 1;

  try {
    const params = {
      school_name: orgName || "",
      u_name: requesterName || "",
      receipt_date_from: convertMonthToApiFormat(startDate),
      receipt_date_to: convertMonthToApiFormat(endDate)
    };

    const result = await requestApi.selectASRequest(params);

    const techList = Array.isArray(result?.tech_as_list)
      ? result.tech_as_list
      : [];
    const blendList = Array.isArray(result?.blend_as_list)
      ? result.blend_as_list
      : [];

    techRows.value = techList.map((item, index) =>
      normalizeASRow(item, index, "tech")
    );

    blendRows.value = blendList.map((item, index) =>
      normalizeASRow(item, index, "blend")
    );

    const uniqueSchools = [
      ...new Set(
        [...blendRows.value, ...techRows.value]
          .map((row) => row.schoolName)
          .filter(Boolean)
      )
    ];

    if (uniqueSchools.length > 1) {
      showSchoolSelector.value = true;
    }
  } catch (error) {
    console.error("AS 내역 조회 실패:", error);
    techRows.value = [];
    blendRows.value = [];
    showSchoolSelector.value = false;
    selectedSchoolFilter.value = "";
    showToast?.("A/S 내역 조회 중 오류가 발생했습니다.");
  } finally {
    isLoading.value = false;
  }
}

async function handleSearch() {
  const orgName = searchForm.value.orgName.trim();
  const requesterName = searchForm.value.requesterName.trim();

  clearSearchErrors();

  if (!orgName || !requesterName) {
    if (!orgName) {
      fieldErrors.value.orgName = "학교 / 기관명을 입력해 주세요.";
    }

    if (!requesterName) {
      fieldErrors.value.requesterName = "접수자 이름을 입력해 주세요.";
    }

    guideState.value = "error";
    guideMessage.value = "검색어를 모두 입력해 주세요.";
    focusFirstInvalidField();
    return;
  }

  if (ENABLE_SEARCH_GUARD) {
    if (!hasEnoughLength(orgName, MIN_ORG_LENGTH)) {
      fieldErrors.value.orgName =
        "학교 / 기관명은 2글자 이상 구체적으로 입력해 주세요.";
      guideState.value = "error";
      guideMessage.value = "학교 / 기관명은 너무 짧게 입력할 수 없습니다.";
      focusFirstInvalidField();
      return;
    }

    if (!hasEnoughLength(requesterName, MIN_NAME_LENGTH)) {
      fieldErrors.value.requesterName =
        "접수자 이름은 2글자 이상 입력해 주세요.";
      guideState.value = "error";
      guideMessage.value = "접수자 이름은 너무 짧게 입력할 수 없습니다.";
      focusFirstInvalidField();
      return;
    }

    if (isBlockedOrgKeyword(orgName)) {
      fieldErrors.value.orgName = "포괄적인 단어만으로는 검색할 수 없습니다.";
      guideState.value = "error";
      guideMessage.value = `'${orgName}'와 같은 포괄적인 단어만으로는 검색할 수 없습니다.`;
      focusFirstInvalidField();
      return;
    }

    if (isBlockedNameKeyword(requesterName)) {
      fieldErrors.value.requesterName =
        "포괄적인 단어만으로는 검색할 수 없습니다.";
      guideState.value = "error";
      guideMessage.value = `'${requesterName}'과(와) 같은 포괄적인 단어만으로는 검색할 수 없습니다.`;
      focusFirstInvalidField();
      return;
    }
  }

  guideState.value = "default";
  guideMessage.value = "";

  if (!dateFilter.value.startDate || !dateFilter.value.endDate) {
    dateFilter.value = getDefaultDateRange();
  }

  searchedInfo.value = {
    orgName,
    requesterName
  };

  hasSearched.value = true;

  await router.replace({
    query: {
      orgName,
      requesterName,
      startDate: dateFilter.value.startDate,
      endDate: dateFilter.value.endDate
    }
  });

  await getASRequest(
    orgName,
    requesterName,
    dateFilter.value.startDate,
    dateFilter.value.endDate
  );
}

async function handleDateFilterChange() {
  if (!hasSearched.value) return;
  if (!dateFilter.value.startDate || !dateFilter.value.endDate) return;

  techPage.value = 1;
  blendPage.value = 1;

  await router.replace({
    query: {
      orgName: searchedInfo.value.orgName,
      requesterName: searchedInfo.value.requesterName,
      startDate: dateFilter.value.startDate,
      endDate: dateFilter.value.endDate
    }
  });

  await getASRequest(
    searchedInfo.value.orgName,
    searchedInfo.value.requesterName,
    dateFilter.value.startDate,
    dateFilter.value.endDate
  );
}

async function fallbackCopyText(text) {
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.setAttribute("readonly", "");
  textArea.style.position = "fixed";
  textArea.style.top = "-9999px";
  textArea.style.left = "-9999px";
  textArea.style.opacity = "0";

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  let copied = false;

  try {
    copied = document.execCommand("copy");
  } catch (error) {
    copied = false;
  } finally {
    document.body.removeChild(textArea);
  }

  return copied;
}

async function copySearchUrl() {
  try {
    isCopyingUrl.value = true;

    const url = `${window.location.origin}${
      route.path
    }?orgName=${encodeURIComponent(
      searchedInfo.value.orgName || ""
    )}&requesterName=${encodeURIComponent(
      searchedInfo.value.requesterName || ""
    )}&startDate=${encodeURIComponent(
      dateFilter.value.startDate || ""
    )}&endDate=${encodeURIComponent(dateFilter.value.endDate || "")}`;

    let copied = false;

    if (window.isSecureContext && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      copied = true;
    } else {
      copied = await fallbackCopyText(url);
    }

    if (copied) {
      showToast?.("URL이 복사되었습니다.");
    } else {
      showToast?.("URL 복사에 실패했습니다.");
    }
  } catch (error) {
    console.error("URL 복사 실패:", error);

    const url = `${window.location.origin}${
      route.path
    }?orgName=${encodeURIComponent(
      searchedInfo.value.orgName || ""
    )}&requesterName=${encodeURIComponent(
      searchedInfo.value.requesterName || ""
    )}&startDate=${encodeURIComponent(
      dateFilter.value.startDate || ""
    )}&endDate=${encodeURIComponent(dateFilter.value.endDate || "")}`;

    const copied = await fallbackCopyText(url);
    showToast?.(copied ? "URL이 복사되었습니다." : "URL 복사에 실패했습니다.");
  } finally {
    isCopyingUrl.value = false;
  }
}

function applyRouteQuery() {
  const orgName = String(route.query.orgName || "").trim();
  const requesterName = String(route.query.requesterName || "").trim();
  const startDate = String(route.query.startDate || "").trim();
  const endDate = String(route.query.endDate || "").trim();

  const defaultRange = getDefaultDateRange();

  dateFilter.value = {
    startDate: startDate || defaultRange.startDate,
    endDate: endDate || defaultRange.endDate
  };

  searchForm.value.orgName = orgName;
  searchForm.value.requesterName = requesterName;

  if (!orgName || !requesterName) {
    hasSearched.value = false;
    return;
  }

  searchedInfo.value = {
    orgName,
    requesterName
  };

  hasSearched.value = true;

  getASRequest(
    orgName,
    requesterName,
    dateFilter.value.startDate,
    dateFilter.value.endDate
  );
}

async function resetSearch() {
  hasSearched.value = false;
  techRows.value = [];
  blendRows.value = [];
  selectedRow.value = null;
  detailOpen.value = false;
  showSchoolSelector.value = false;
  selectedSchoolFilter.value = "";
  techPage.value = 1;
  blendPage.value = 1;

  closePdfPreview();

  searchForm.value.orgName = "";
  searchForm.value.requesterName = "";
  searchedInfo.value = {
    orgName: "",
    requesterName: ""
  };

  dateFilter.value = getDefaultDateRange();

  clearSearchErrors();

  await router.replace({ query: {} });
}

function openDetail(row) {
  selectedRow.value = row;
  detailOpen.value = true;
}

function closeDetail() {
  detailOpen.value = false;
}

function displayProgressStatus(value) {
  return normalizeProgressStatus(value) || "-";
}

function statusClass(value) {
  const text = normalizeProgressStatus(value);

  if (text.includes("완료")) return "done";
  if (text.includes("대기")) return "waiting";
  if (text.includes("진행")) return "working";
  if (text.includes("업체완료")) return "done";

  return "default";
}

function closePdfPreview() {
  pdfPreviewOpen.value = false;
  currentPdfBlob.value = null;
  pdfPreviewImageUrl.value = "";
}

async function buildPdfBlob() {
  await nextTick();

  const target = pdfCaptureRef.value;
  if (!target) {
    throw new Error("PDF 캡처 대상이 없습니다.");
  }

  const canvas = await html2canvas(target, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    logging: false
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF("p", "mm", "a4");

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  const margin = 10;
  const usableWidth = pageWidth - margin * 2;

  const imgWidth = usableWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;

  let heightLeft = imgHeight;
  let position = margin;

  pdf.addImage(
    imgData,
    "PNG",
    margin,
    position,
    imgWidth,
    imgHeight,
    "",
    "FAST"
  );
  heightLeft -= pageHeight - margin * 2;

  while (heightLeft > 0) {
    position = margin - (imgHeight - heightLeft);
    pdf.addPage();
    pdf.addImage(
      imgData,
      "PNG",
      margin,
      position,
      imgWidth,
      imgHeight,
      "",
      "FAST"
    );
    heightLeft -= pageHeight - margin * 2;
  }

  return pdf.output("blob");
}

async function openPdfPreview() {
  if (!selectedRow.value || isGeneratingPdf.value) return;

  try {
    isGeneratingPdf.value = true;

    pdfPreviewImageUrl.value = "";

    await nextTick();

    const target = pdfCaptureRef.value;
    if (!target) {
      throw new Error("PDF 캡처 대상이 없습니다.");
    }

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false
    });

    pdfPreviewImageUrl.value = canvas.toDataURL("image/png");

    const blob = await buildPdfBlob();
    currentPdfBlob.value = blob;

    pdfPreviewOpen.value = true;
  } catch (error) {
    console.error("PDF 미리보기 생성 실패:", error);
    showToast?.("PDF 미리보기 생성 중 오류가 발생했습니다.");
  } finally {
    isGeneratingPdf.value = false;
  }
}

async function downloadCurrentPdf() {
  try {
    if (!currentPdfBlob.value) {
      const blob = await buildPdfBlob();
      currentPdfBlob.value = blob;
    }

    const fileName = [
      "AS상세내역",
      selectedRow.value?.serviceLabel || "",
      selectedRow.value?.schoolName || ""
    ]
      .filter(Boolean)
      .join("_")
      .replace(/[\\/:*?"<>|]+/g, "_");

    const url = URL.createObjectURL(currentPdfBlob.value);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${fileName}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("PDF 다운로드 실패:", error);
    showToast?.("PDF 다운로드 중 오류가 발생했습니다.");
  }
}

watch(detailOpen, (opened) => {
  if (!opened) {
    closePdfPreview();
  }
});

onMounted(() => {
  dateFilter.value = getDefaultDateRange();
  applyRouteQuery();
});
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: #f6f7f9;
  color: #1f2937;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

.top-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

.header-inner {
  max-width: 1440px;
  height: 96px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.brand-icon {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  overflow: hidden;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.brand-title {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  line-height: 1.1;
}

.brand-sub {
  font-size: 13px;
  color: #6b7280;
  line-height: 1;
}

.header-center {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-self: center;
}

.menu-btn {
  height: 44px;
  padding: 0 22px;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #4b5563;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
}

.menu-btn.active {
  color: #111827;
  border-bottom-color: #18a0fb;
}

.header-right {
  min-height: 1px;
}

.content-wrap {
  max-width: 1440px;
  margin: 0 auto;
  padding: 48px 24px 60px;
}

.content-card {
  min-height: 680px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  padding: 52px 56px;
}

.content-head {
  margin-bottom: 28px;
}

.page-title {
  margin: 0;
  font-size: 48px;
  font-weight: 800;
  color: #111827;
  line-height: 1.15;
}

.search-panel {
  padding: 36px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.search-icon-wrap {
  margin-bottom: 24px;
}

.search-icon-circle {
  width: 88px;
  height: 88px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.search-icon-img {
  width: 42px;
  height: 42px;
  object-fit: contain;
}

.search-desc {
  margin: 0 0 40px;
  font-size: 19px;
  line-height: 1.8;
  color: #6b7280;
}

.search-form {
  width: 100%;
  max-width: 1040px;
  display: grid;
  grid-template-columns: 1.4fr 1.1fr 180px;
  gap: 16px;
  align-items: start;
}

.search-field {
  width: 100%;
  text-align: left;
}

.search-input {
  width: 100%;
  height: 64px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  padding: 0 20px;
  font-size: 18px;
  color: #111827;
  outline: none;
}

.search-input:focus,
.tool-input:focus {
  border-color: #18a0fb;
  box-shadow: 0 0 0 4px rgba(24, 160, 251, 0.1);
}

.search-input.is-error {
  border-color: #dc2626;
  background: #fef2f2;
}

.search-input.is-error:focus {
  border-color: #dc2626;
  box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.12);
}

.field-error-text {
  margin: 8px 0 0;
  font-size: 13px;
  color: #dc2626;
  line-height: 1.5;
}

.search-submit {
  height: 64px;
  border: none;
  background: #111827;
  color: #ffffff;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
}

.search-submit:disabled {
  opacity: 0.7;
  cursor: default;
}

.search-guide-box {
  width: 100%;
  max-width: 1040px;
  margin-top: 18px;
  padding: 18px 20px;
  border: 1px solid #f4bf00;
  background: #fff8db;
  text-align: left;
  box-shadow: 0 8px 20px rgba(244, 191, 0, 0.12);
}

.search-guide-box.is-error {
  border-color: #fca5a5;
  background: #fef2f2;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.08);
}

.search-guide-title {
  font-size: 16px;
  font-weight: 800;
  color: #7c5a00;
  margin-bottom: 10px;
}

.search-guide-box.is-error .search-guide-title {
  color: #991b1b;
}

.search-guide-list {
  margin: 0;
  padding-left: 18px;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.7;
}

.search-guide-list li + li {
  margin-top: 6px;
}

.search-guide-alert {
  margin: 0;
  font-size: 14px;
  line-height: 1.7;
  color: #991b1b;
  font-weight: 600;
}

.bad-example {
  color: #b45309;
  font-weight: 700;
}

.search-guard-off-badge {
  margin-top: 14px;
  display: inline-block;
  padding: 8px 12px;
  border-radius: 999px;
  background: #111827;
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
}

.notice-list {
  width: 100%;
  max-width: 1040px;
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid #d1d5db;
  text-align: left;
}

.notice-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.notice-item + .notice-item {
  margin-top: 14px;
}

.notice-icon {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: #6b7280;
  color: #ffffff;
  font-size: 14px;
  font-weight: 800;
  line-height: 24px;
  text-align: center;
  flex-shrink: 0;
}

.notice-item--accent .notice-icon {
  background: #f4bf00;
  color: #ffffff;
}

.notice-text {
  font-size: 17px;
  line-height: 1.7;
  color: #374151;
  word-break: keep-all;
}

.notice-sub {
  color: #6b7280;
  margin-left: 6px;
}

.side-floating-menu {
  position: fixed;
  top: 50%;
  left: calc(50% + 720px + ((100vw - 1440px) / 4) - 43px);
  transform: translateY(-50%);
  z-index: 40;
  width: 86px;
  display: none;
  flex-direction: column;
  align-items: center;
}

.floating-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
}

.floating-icon {
  width: 86px;
  height: 86px;
  border-radius: 999px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
}

.floating-icon--qr {
  background: #fee500;
  border-color: #f2d600;
}

.floating-icon--qr img {
  width: 50px;
  height: 50px;
  object-fit: contain;
  display: block;
}

.floating-label {
  margin-top: 10px;
  font-size: 15px;
  font-weight: 700;
  color: #374151;
  text-align: center;
  line-height: 1.35;
  white-space: nowrap;
}

@media (min-width: 1526px) {
  .side-floating-menu {
    display: flex;
  }
}

.result-panel {
  padding-top: 12px;
}

.result-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 20px;
}

.result-head-left {
  min-width: 0;
}

.result-title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #111827;
}

.result-desc {
  margin: 8px 0 0;
  font-size: 15px;
  color: #6b7280;
}

.divider-dot {
  margin: 0 8px;
}

.result-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.month-range-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.tool-input {
  height: 44px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  font-size: 14px;
  color: #111827;
  outline: none;
}

.month-input {
  min-width: 150px;
}

.range-tilde {
  font-size: 16px;
  font-weight: 800;
  color: #6b7280;
  line-height: 1;
}

.tool-btn,
.reset-btn {
  height: 44px;
  padding: 0 16px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.tool-btn:disabled {
  opacity: 0.6;
  cursor: default;
}

.school-selector-card {
  margin-bottom: 18px;
  padding: 18px 20px;
  border: 1px solid #dbe3ec;
  background: #f8fafc;
}

.school-selector-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
}

.school-selector-desc {
  margin-top: 6px;
  font-size: 14px;
  color: #6b7280;
}

.school-selector-list {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.school-selector-btn {
  height: 40px;
  padding: 0 14px;
  border: 1px solid #cfd8e3;
  background: #ffffff;
  color: #374151;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.school-selector-btn.active {
  border-color: #18a0fb;
  background: #eef6ff;
  color: #0f5ea8;
}

.result-loading-wrap,
.empty-result-card {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  background: #fbfcfd;
}

.result-section + .result-section {
  margin-top: 28px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-title-lg {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
}

.section-count {
  font-size: 14px;
  color: #6b7280;
  font-weight: 700;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none !important;
}

.table-wrap {
  overflow-x: auto;
  border-top: 2px solid #111827;
}

.intake-table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.intake-table thead th {
  background: #f9fafb;
  color: #374151;
  font-size: 14px;
  font-weight: 800;
  text-align: left;
  padding: 16px 14px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.intake-table tbody td {
  padding: 16px 14px;
  border-bottom: 1px solid #eef2f7;
  vertical-align: middle;
  font-size: 14px;
  background: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.col-date {
  width: 10%;
}

.col-product {
  width: 15%;
}

.col-model {
  width: 15%;
}

.col-content {
  width: 50%;
}

.col-progress {
  width: 10%;
}

.ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wide-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.data-row {
  cursor: pointer;
  transition: background 0.2s ease;
}

.data-row:hover td {
  background: #f8fafc;
}

.data-row.active td {
  background: #eef6ff;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;
}

.status-chip.default {
  background: #f1f5f9;
  color: #475569;
}

.status-chip.waiting {
  background: #fff7ed;
  color: #c2410c;
}

.status-chip.working {
  background: #eff6ff;
  color: #1d4ed8;
}

.status-chip.done {
  background: #ecfdf5;
  color: #047857;
}

.empty-row,
.loading-row {
  text-align: center;
  color: #6b7280;
  padding: 36px 16px !important;
}

.loading-box {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #dbe3ec;
  border-top-color: #111827;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 0 6px;
  flex-wrap: wrap;
}

.page-btn,
.page-number {
  min-width: 40px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

.page-number.active {
  background: #111827;
  border-color: #111827;
  color: #fff;
}

.page-btn:disabled {
  opacity: 0.45;
  cursor: default;
}

.page-summary {
  font-size: 14px;
  font-weight: 700;
  color: #4b5563;
}

.mobile-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mobile-item {
  padding: 16px;
  border: 1px solid #e5e7eb;
  background: #fff;
  cursor: pointer;
}

.mobile-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

.mobile-school {
  font-size: 16px;
  font-weight: 800;
  line-height: 1.4;
  color: #111827;
}

.mobile-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 12px;
}

.mobile-issue {
  font-size: 13px;
  line-height: 1.5;
  color: #374151;
  background: #f8fafc;
  padding: 10px 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin-top: 8px;
}

.pagination-mobile {
  padding-top: 14px;
}

.drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  justify-content: flex-end;
}

.detail-drawer {
  width: min(920px, 92vw);
  height: 100%;
  background: #f5f7fa;
  border-left: 1px solid #d9dee5;
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.16);
  display: flex;
  flex-direction: column;
}

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid #dde3ea;
  background: #ffffff;
}

.drawer-header h2 {
  margin: 4px 0 0;
  font-size: 24px;
  font-weight: 800;
}

.drawer-kicker {
  font-size: 12px;
  font-weight: 900;
  color: #2563eb;
  letter-spacing: 0.08em;
}

.drawer-sub {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.drawer-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.drawer-tool-btn {
  white-space: nowrap;
}

.icon-btn {
  cursor: pointer;
  width: 40px;
  height: 40px;
  border: 1px solid #dbe3ec;
  background: #fff;
  font-size: 18px;
}

.drawer-body {
  padding: 16px;
  overflow: auto;
}

.table-section {
  background: #fff;
  border: 1px solid #d9dee5;
}

.section-title {
  margin: 0;
  padding: 12px 14px;
  font-size: 14px;
  font-weight: 800;
  color: #1f2937;
  background: #f3f6fa;
  border-bottom: 1px solid #d9dee5;
}

.detail-form-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.detail-form-table th,
.detail-form-table td {
  border: 1px solid #d9dee5;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.45;
  vertical-align: middle;
  word-break: break-word;
}

.detail-form-table th {
  width: 130px;
  background: #eef1f5;
  color: #4b5563;
  font-weight: 700;
  text-align: left;
}

.detail-form-table td {
  background: #ffffff;
  color: #111827;
}

.multiline-cell {
  white-space: pre-wrap;
  word-break: break-word;
}

/* PDF 캡처 영역 */
.pdf-capture-wrap {
  position: fixed;
  left: -100000px;
  top: 0;
  width: 900px;
  pointer-events: none;
  opacity: 1;
  z-index: -1;
}

.pdf-sheet {
  width: 794px;
  background: #ffffff;
  color: #111827;
  padding: 32px;
}

.pdf-sheet-head {
  margin-bottom: 20px;
}

.pdf-brand {
  font-size: 14px;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 10px;
}

.pdf-title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: #111827;
}

.pdf-sub {
  margin-top: 8px;
  font-size: 14px;
  color: #6b7280;
}

.pdf-detail-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  border-top: 2px solid #111827;
}

.pdf-detail-table th,
.pdf-detail-table td {
  border: 1px solid #d9dee5;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.6;
  vertical-align: top;
  word-break: break-word;
  background: #ffffff;
}

.pdf-detail-table th {
  width: 120px;
  background: #f3f6fa;
  color: #374151;
  font-weight: 800;
  text-align: left;
}

.pdf-footer {
  margin-top: 16px;
  text-align: right;
  font-size: 12px;
  color: #6b7280;
}

/* PDF 미리보기 */
.pdf-preview-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.pdf-preview-modal {
  width: min(1200px, 96vw);
  height: min(90vh, 920px);
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 64px rgba(15, 23, 42, 0.28);
}

.pdf-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
}

.pdf-preview-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pdf-preview-body {
  flex: 1;
  min-height: 0;
  background: #eef2f7;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.pdf-preview-frame {
  width: 100%;
  height: 100%;
  border: none;
  background: #ffffff;
}

.pdf-preview-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

.pdf-preview-image {
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
  background: #ffffff;
}

.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.22s ease;
}

.drawer-fade-enter-from,
.drawer-fade-leave-to {
  opacity: 0;
}

.desktop-detail-table {
  display: table;
}

.mobile-detail-list {
  display: none;
}

.mobile-detail-item {
  border-top: 1px solid #d9dee5;
}

.mobile-detail-item:first-child {
  border-top: none;
}

.mobile-detail-label {
  padding: 12px 14px;
  background: #eef1f5;
  color: #4b5563;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.45;
}

.mobile-detail-value {
  padding: 12px 14px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  line-height: 1.55;
  word-break: break-word;
  white-space: pre-wrap;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .header-inner {
    grid-template-columns: 1fr;
    gap: 14px;
    height: auto;
    padding: 16px;
  }

  .header-center {
    justify-self: start;
  }

  .content-wrap {
    padding: 24px 16px 40px;
  }

  .content-card {
    padding: 28px 20px;
  }

  .page-title {
    font-size: 34px;
  }

  .search-desc {
    font-size: 16px;
  }

  .search-form {
    grid-template-columns: 1fr;
  }

  .notice-text {
    font-size: 15px;
  }

  .notice-sub {
    display: block;
    margin-left: 0;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .detail-drawer {
    width: 100vw;
  }

  .mobile-meta {
    grid-template-columns: 1fr;
  }

  .side-floating-menu {
    display: none !important;
  }

  .section-head,
  .result-top {
    align-items: flex-start;
    flex-direction: column;
  }

  .result-tools {
    width: 100%;
    justify-content: flex-start;
  }

  .pdf-preview-modal {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .pdf-preview-backdrop {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }

  .pdf-preview-modal {
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
  }

  .pdf-preview-body {
    overflow: auto;
  }

  .pdf-preview-image {
    width: 100%;
    max-width: none;
  }
}

@media (max-width: 640px) {
  .brand-title {
    font-size: 18px;
  }

  .brand-sub {
    font-size: 12px;
  }

  .menu-btn {
    padding: 0 12px;
    font-size: 15px;
  }

  .page-title {
    font-size: 28px;
  }

  .search-desc {
    font-size: 15px;
    margin-bottom: 28px;
  }

  .search-input,
  .search-submit {
    height: 56px;
    font-size: 16px;
  }

  .search-guide-box {
    padding: 14px 16px;
    margin-top: 16px;
  }

  .search-guide-title {
    font-size: 15px;
  }

  .search-guide-list,
  .search-guide-alert {
    font-size: 13px;
    line-height: 1.6;
  }

  .notice-list {
    margin-top: 20px;
    padding-top: 14px;
  }

  .notice-item {
    gap: 10px;
  }

  .notice-icon {
    width: 22px;
    height: 22px;
    font-size: 13px;
    line-height: 22px;
  }

  .notice-text {
    font-size: 14px;
    line-height: 1.6;
  }

  .section-title-lg {
    font-size: 19px;
  }

  .result-tools {
    flex-direction: column;
    align-items: stretch;
  }

  .month-range-wrap {
    width: 100%;
  }

  .month-input,
  .tool-btn,
  .reset-btn {
    width: 100%;
  }

  .range-tilde {
    flex: 0 0 auto;
    align-self: center;
  }

  .drawer-header {
    flex-direction: column;
    align-items: stretch;
  }

  .drawer-header-actions {
    justify-content: space-between;
  }

  .desktop-detail-table {
    display: none;
  }

  .mobile-detail-list {
    display: block;
  }

  .mobile-detail-label,
  .mobile-detail-value {
    font-size: 14px;
    padding: 14px 16px;
  }
}
</style>
